import React, { useEffect, useState } from "react";

import { supabase } from "../../lib/supabaseClient";

import { useSnackbar } from "notistack";
import { DayPicker } from "react-day-picker";
import dayjs, { Dayjs } from "dayjs";
import { isoToWallClock, wallClockToISO } from "../../utils/NewYorkTime";
import DaySchedule from "./components/DaySchedule";
import LoginGate from "../../components/ui/content/LoginGate";
import { Helmet } from "react-helmet";

type CachedMeetings = {
    [date: string]: CalendarMeeting[];
};

const Index = () => {
    const { enqueueSnackbar } = useSnackbar();

    const [cachedMeetings, setCachedMeetings] = useState<CachedMeetings>({});
    const [meetings, setMeetings] = useState<CalendarMeeting[]>([]);

    const [time, setTime] = useState<Dayjs>(isoToWallClock().startOf("day"));

    const [currentMonth, setCurrentMonth] = useState<Date>(
        new Date(time.year(), time.month(), 1),
    );

    const [loading, setLoading] = useState(false);
    const [highlightedDays, setHighlightedDays] = useState<number[]>([]);

    const updateHighlightedDays = async (month: number, year: number) => {
        const { data, error } = await supabase.rpc("get_unique_meeting_days", {
            month: month + 1,
            year,
        });

        if (!data) {
            setHighlightedDays([]);
            setLoading(false);
            return;
        }

        if (error) {
            enqueueSnackbar("Error fetching meeting days", {
                variant: "error",
            });

            setHighlightedDays([]);
            return;
        }

        setHighlightedDays(data as number[]);
        setLoading(false);
    };

    useEffect(() => {
        let dayString = `${time.year()}/${time.month()}/${time.date()}`;
        if (cachedMeetings[dayString]) {
            return setMeetings(cachedMeetings[dayString]);
        }

        let dayStart = wallClockToISO(time.startOf("day"));
        let nextDayStart = wallClockToISO(time.add(1, "day").startOf("day"));

        updateHighlightedDays(time.month(), time.year());

        const fetchMeetings = async () => {
            const { data, error } = await supabase
                .from("meetings")
                .select(
                    `
          id,
          title,
          description,
          is_public,
          start_time,
          end_time,
          rooms (
            id,
            name,
            floor
          ),
          organizations!inner (
            id,
            url,
            name,
            picture
          ),
          advisor
        `,
                )
                .neq("organizations.url", "epsilon") // internal maintenance meetings
                .gte("start_time", dayStart)
                .lt("start_time", nextDayStart);

            if (error || !data) {
                return enqueueSnackbar(
                    "Error fetching meetings. Contact it@stuysu.org for support.",
                    { variant: "error" },
                );
            }

            setCachedMeetings({ ...cachedMeetings, [dayString]: data as any });
            setMeetings(data as any);
        };

        fetchMeetings();
    }, [time]);

    return (
        <div>
            <Helmet>
                <title>Calendar - Epsilon</title>
                <meta
                    name="description"
                    content="Happenings at Stuyvesant High School."
                />
            </Helmet>
            <LoginGate sx={{ width: "100%" }} page={"view the calendar"}>
                <div
                    className={
                        "m-4 sm:m-12 flex items-start gap-8 min-h-screen max-md:flex-col"
                    }
                >
                    <DayPicker
                        mode="single"
                        month={currentMonth}
                        selected={time.toDate()}
                        today={isoToWallClock().toDate()}
                        onSelect={(date) => {
                            if (!date) return;
                            setTime(dayjs(date));
                        }}
                        onMonthChange={(month) => {
                            setCurrentMonth(month);
                            setLoading(true);
                            updateHighlightedDays(
                                month.getMonth(),
                                month.getFullYear(),
                            );
                        }}
                        showOutsideDays
                        weekStartsOn={1}
                        classNames={{
                            month_grid: "max-sm:scale-125 origin-top-left",
                            button_previous:
                                "fill-typography-1 z-10 relative sm:hover:bg-layer-2 rounded-full",
                            button_next:
                                "fill-typography-1 z-10 relative sm:hover:bg-layer-2 rounded-full",
                            root: "max-sm:pb-16 p-5 rounded-xl p-3 bg-layer-1 shadow-control relative max-md:w-full",
                            nav: "absolute flex gap-2 ml-16 right-5 top-3.5",
                            month_caption:
                                "relative -top-1 mb-3 text-typography-1 important",
                            weekday: "font-normal text-typography-2",
                            day: "p-0 text-center text-typography-1",
                            day_button:
                                "sm:w-10 sm:h-10 w-8 h-8 grid place-items-center rounded-md transition-colors sm:hover:bg-layer-2",
                            selected:
                                "bg-accent text-typography-1 rounded-md pointer-events-none",
                            outside: "opacity-50 pointer-events-none",
                            today: "important rounded-md",
                        }}
                        /* underline markers for meeting days */
                        modifiers={{
                            hasMeetings: (date) =>
                                date.getMonth() === currentMonth.getMonth() &&
                                date.getFullYear() ===
                                    currentMonth.getFullYear() &&
                                highlightedDays.includes(date.getDate()),
                        }}
                        modifiersClassNames={{
                            hasMeetings:
                                'relative after:content-[""] after:left-1/2 after:absolute after:-translate-x-1/2 after:bottom-1 after:w-1 after:h-1 after:rounded-full after:bg-yellow',
                        }}
                    />

                    <div className={"w-full"}>
                        <DaySchedule day={time} meetings={meetings} />
                    </div>
                </div>
            </LoginGate>
        </div>
    );
};

export default Index;
